import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MetricBucket,
  MetricCatalogEntry,
  MetricGroup,
  MetricsResponse,
  MetricUnit,
  formatMetric,
} from "./useMetrics";
import DeltaChip from "./DeltaChip";

const MAGENTA = "#9E1F63FF";
const MAGENTA_TINT = "#F9E6F0";

type TableRowDef = {
  key: string;
  label: string;
  unit: MetricUnit;
  definition?: string;
  /** Catalog metric this row renders; absent for keyed-breakdown rows. */
  entry?: MetricCatalogEntry;
  referralType?: string;
  assistanceType?: string;
};

type SectionDef = { title: string; rows: TableRowDef[] };

/** Breakdown rows have no catalog metric behind them: no drilldown. */
const isBreakdownRow = (row: TableRowDef) =>
  Boolean(row.referralType || row.assistanceType);

const bucketValue = (
  bucket: MetricBucket | undefined | null,
  row: TableRowDef,
): number => {
  if (!bucket) return 0;
  const v = row.referralType
    ? (bucket.referralTypes || {})[row.referralType]
    : row.assistanceType
      ? (bucket.assistanceRequested || {})[row.assistanceType]
      : bucket[row.entry ? row.entry.id : row.key];
  return typeof v === "number" && !Number.isNaN(v) ? v : 0;
};

export interface MetricTableProps {
  data: MetricsResponse | null;
  loading?: boolean;
  error?: string | null;
  /** Click on a group (column) header — used to open a consultant detail. */
  onGroupClick?: (group: MetricGroup) => void;
  /** Click on any number — used to open the drilldown drawer. */
  onCellClick?: (row: { id: string; label: string }, group: MetricGroup | null) => void;
  exportName?: string;
  maxHeight?: number | string;
  /**
   * Previous-period metrics for the same query. When present the Total column
   * grows a "prev: N (±x%)" second line per row; the consultant columns stay
   * deliberately uncluttered.
   */
  previous?: MetricsResponse | null;
  /** Label of the previous period, surfaced in the delta tooltip. */
  previousLabel?: string;
}

/**
 * Sectioned consultants x metrics table. Sticky metric column, section header
 * rows, bold column max, "–" for zero, Copy + CSV export. Adapted from the
 * Program Statistics SbcStatsModal body.
 */
const MetricTable: React.FC<MetricTableProps> = ({
  data,
  loading,
  error,
  onGroupClick,
  onCellClick,
  exportName = "metrics",
  maxHeight = "60vh",
  previous = null,
  previousLabel,
}) => {
  const [snack, setSnack] = useState<string | null>(null);
  const groups: MetricGroup[] = data?.groups || [];
  // Metric + consultant columns + Total.
  const columnCount = groups.length + 2;

  const sections: SectionDef[] = useMemo(() => {
    if (!data) return [];
    const bySection: SectionDef[] = [];
    const index = new Map<string, SectionDef>();
    (data.catalog || []).forEach((entry) => {
      // These catalog entries are keyed objects, rendered in their own
      // category sections below rather than as empty numeric rows.
      if (entry.id === "referralTypes" || entry.id === "assistanceRequested") {
        return;
      }
      const title = entry.section || "Metrics";
      let section = index.get(title);
      if (!section) {
        section = { title, rows: [] };
        index.set(title, section);
        bySection.push(section);
      }
      section.rows.push({
        key: entry.id,
        label: entry.label,
        unit: entry.unit,
        definition: entry.definition,
        entry,
      });
    });
    // Referral-type breakdown ships as a nested map rather than catalog rows.
    const typeKeys = Object.keys(data.total?.referralTypes || {});
    if (typeKeys.length > 0) {
      bySection.push({
        title: "Referral Types",
        rows: typeKeys.map((type) => ({
          key: `referralType:${type}`,
          label: type,
          unit: "count" as MetricUnit,
          referralType: type,
        })),
      });
    }
    // Same for assistance demand — absent entirely on an older server.
    const assistanceKeys = Object.keys(data.total?.assistanceRequested || {});
    if (assistanceKeys.length > 0) {
      bySection.push({
        title: "Assistance Requested",
        rows: assistanceKeys.map((category) => ({
          key: `assistance:${category}`,
          label: category,
          unit: "count" as MetricUnit,
          assistanceType: category,
        })),
      });
    }
    return bySection;
  }, [data]);

  // The exported grid is deliberately the plain numbers only: the
  // previous-period line is an on-screen affordance and stays out of the
  // CSV / clipboard copy, so downstream sheets keep their shape.
  const buildGrid = (): string[][] => {
    const header = ["Metric", ...groups.map((g) => g.label), "Total"];
    const grid: string[][] = [header];
    sections.forEach((section) => {
      grid.push([section.title]);
      section.rows.forEach((row) => {
        const asText = (v: number) =>
          row.unit === "hours" ? v.toFixed(1) : String(v);
        grid.push([
          row.label,
          ...groups.map((g) => asText(bucketValue(g.metrics, row))),
          asText(bucketValue(data?.total, row)),
        ]);
      });
    });
    return grid;
  };

  const copyTable = async () => {
    const text = buildGrid()
      .map((r) => r.join("\t"))
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setSnack("Table copied to clipboard");
    } catch {
      setSnack("Could not copy to clipboard");
    }
  };

  const downloadCsv = () => {
    const csvEscape = (s: string) =>
      /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    const csv = buildGrid()
      .map((r) => r.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const range = `${data?.range?.start || "all"}_${data?.range?.end || "today"}`;
    a.download = `${exportName}_${range}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const numberCellSx = { textAlign: "right", whiteSpace: "nowrap" } as const;

  const renderNumber = (
    value: number,
    row: TableRowDef,
    group: MetricGroup | null,
    isMax?: boolean,
  ) => {
    const clickable =
      Boolean(onCellClick) && !isBreakdownRow(row) && value !== 0;
    const content =
      value === 0 ? (
        <span style={{ color: "#9e9e9e" }}>–</span>
      ) : (
        <span style={isMax ? { fontWeight: 700 } : undefined}>
          {formatMetric(value, row.unit)}
        </span>
      );
    if (!clickable) return content;
    return (
      <Box
        component="span"
        onClick={() => onCellClick && onCellClick({ id: row.key, label: row.label }, group)}
        sx={{
          cursor: "pointer",
          color: MAGENTA,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {content}
      </Box>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 6 }}
        >
          <CircularProgress size={22} sx={{ color: MAGENTA }} />
          <Typography variant="body2" color="text.secondary">
            Loading metrics…
          </Typography>
        </Stack>
      )}

      {data && (
        <>
          <TableContainer sx={{ overflow: "auto", maxHeight }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      minWidth: 220,
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      backgroundColor: "#fff",
                    }}
                  >
                    Metric
                  </TableCell>
                  {groups.map((group) => (
                    <TableCell
                      key={group.key}
                      sx={{ ...numberCellSx, verticalAlign: "top" }}
                    >
                      <Box
                        onClick={() => onGroupClick && onGroupClick(group)}
                        sx={{
                          fontWeight: 700,
                          cursor: onGroupClick ? "pointer" : "default",
                          color: onGroupClick ? MAGENTA : "inherit",
                          "&:hover": onGroupClick
                            ? { textDecoration: "underline" }
                            : undefined,
                        }}
                      >
                        {group.label}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...numberCellSx, fontWeight: 700 }}>
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sections.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount}>
                      No metrics available for this range.
                    </TableCell>
                  </TableRow>
                )}
                {sections.map((section) => (
                  <React.Fragment key={section.title}>
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        sx={{
                          backgroundColor: "#f0f0f0",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          letterSpacing: 0.5,
                        }}
                      >
                        {section.title}
                      </TableCell>
                    </TableRow>
                    {section.rows.map((row) => {
                      const values = groups.map((g) =>
                        bucketValue(g.metrics, row),
                      );
                      const max = Math.max(0, ...values);
                      const total = bucketValue(data.total, row);
                      return (
                        <TableRow key={`${section.title}-${row.key}`} hover>
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            {row.definition ? (
                              <Tooltip title={row.definition} placement="right">
                                <span
                                  style={{
                                    borderBottom: "1px dotted #bdbdbd",
                                    cursor: "help",
                                  }}
                                >
                                  {row.label}
                                </span>
                              </Tooltip>
                            ) : (
                              row.label
                            )}
                          </TableCell>
                          {groups.map((group, i) => {
                            const isMax = max > 0 && values[i] === max;
                            return (
                              <TableCell
                                key={group.key}
                                sx={{
                                  ...numberCellSx,
                                  backgroundColor: isMax
                                    ? MAGENTA_TINT
                                    : undefined,
                                }}
                              >
                                {renderNumber(values[i], row, group, isMax)}
                              </TableCell>
                            );
                          })}
                          <TableCell sx={{ ...numberCellSx, fontWeight: 700 }}>
                            {renderNumber(total, row, null, true)}
                            {previous && (
                              <Box
                                sx={{
                                  fontWeight: 400,
                                  fontSize: "0.7rem",
                                  color: "#8a8a93",
                                  mt: "2px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  gap: 0.5,
                                }}
                              >
                                <span>
                                  prev:{" "}
                                  {formatMetric(
                                    bucketValue(previous.total, row),
                                    row.unit,
                                  )}
                                </span>
                                <DeltaChip
                                  current={total}
                                  previous={bucketValue(previous.total, row)}
                                  unit={row.unit}
                                  periodLabel={previousLabel}
                                />
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ pt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={copyTable}
              sx={{ color: MAGENTA, borderColor: MAGENTA }}
            >
              Copy table
            </Button>
            <Button
              variant="contained"
              onClick={downloadCsv}
              sx={{
                backgroundColor: MAGENTA,
                "&:hover": { backgroundColor: "#7a184c" },
              }}
            >
              Download CSV
            </Button>
          </Stack>
        </>
      )}

      <Snackbar
        open={snack !== null}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </Box>
  );
};

export default MetricTable;
